// AICA ConfirmDialog Component
// Universal confirmation dialog for destructive actions

import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, Info } from 'lucide-react';
import { Card } from '../ui/card';

export interface ConfirmDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  variant?: 'danger' | 'warning' | 'info';
  /**
   * Block confirmation while the dialog cannot state its own consequence —
   * e.g. the impact query behind the message is still loading or failed.
   * Cancel always stays available; a dialog you cannot leave is worse.
   */
  confirmDisabled?: boolean;
  /**
   * Override the confirm button's testid. Needed only when a page mounts
   * more than one ConfirmDialog at once (e.g. records bulk-delete and
   * bulk-start) and a test must target one of them specifically — the
   * default stays the shared id every other call site already relies on.
   */
  confirmButtonTestId?: string;
}

export function ConfirmDialog({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmText,
  cancelText,
  variant = 'warning',
  confirmDisabled = false,
  confirmButtonTestId = 'confirm-dialog-confirm-button',
}: ConfirmDialogProps) {
  const { t } = useTranslation(['common']);
  const dialogRef = useRef<HTMLDivElement | null>(null);
  const cancelRef = useRef<HTMLButtonElement | null>(null);
  const restoreFocusTo = useRef<HTMLElement | null>(null);

  /**
   * `aria-modal="true"` above tells a screen reader the page behind is inert.
   * Nothing enforced that: focus stayed wherever it was, Tab walked straight
   * out into content the same attribute had just declared unavailable, and
   * Escape did nothing. The declaration and the behaviour have to agree —
   * doubly so now that this dialog also guards leaving a settings screen and
   * switching organization, where the thing behind it is a form the user is
   * about to lose.
   *
   * Cancel takes focus, not confirm: this dialog exists in front of
   * destructive or discarding actions, so the safe choice is the one under
   * the cursor when someone hits Enter reflexively.
   */
  useEffect(() => {
    if (!isOpen) return;
    restoreFocusTo.current =
      document.activeElement instanceof HTMLElement ? document.activeElement : null;
    cancelRef.current?.focus();
    return () => {
      restoreFocusTo.current?.focus?.();
    };
  }, [isOpen]);

  /**
   * On the DOCUMENT, not on the container.
   *
   * React's `onKeyDown` only fires for events that bubble through this
   * subtree, and the container is not focusable: one click on the backdrop, on
   * the card, on the title — anywhere that is not a button — leaves focus on
   * `document.body`, and from there both the Escape handler and the focus trap
   * were simply gone. The dialog kept claiming `aria-modal`, Tab walked out
   * into the page behind, and Escape did nothing, for the whole rest of its
   * life. Capture phase so a modal really does take the key ahead of whatever
   * is still mounted behind it.
   */
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        // Claimed before anything under the dialog sees it: a Radix
        // DismissableLayer around it (the assistant's non-modal Sheet)
        // listens on `document` in the capture phase and dismisses unless
        // the event is already defaultPrevented — `stopPropagation` from a
        // later listener on the same node cannot stop it (owner's review,
        // 17.09). Hence `window`, one step ahead, and `preventDefault`.
        event.preventDefault();
        event.stopPropagation();
        onClose();
        return;
      }
      if (event.key !== 'Tab') return;
      const focusable = dialogRef.current?.querySelectorAll<HTMLElement>(
        'button:not([disabled]), [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      if (!focusable || focusable.length === 0) return;
      const first = focusable[0]!;
      const last = focusable[focusable.length - 1]!;
      const active = document.activeElement;
      const inside = !!dialogRef.current?.contains(active);
      // Wrap at both ends rather than letting the browser move focus to the
      // page behind, which is what made the modality claim false. Focus that
      // is not in the dialog at all (the click-on-the-backdrop case) is pulled
      // back in rather than left to walk the page.
      if (event.shiftKey && (active === first || !inside)) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && (active === last || !inside)) {
        event.preventDefault();
        first.focus();
      }
    };

    window.addEventListener('keydown', handleKeyDown, true);
    return () => window.removeEventListener('keydown', handleKeyDown, true);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const actualConfirmText = confirmText || t('common:common.confirm');
  const actualCancelText = cancelText || t('common:common.cancel');

  const handleConfirm = () => {
    onConfirm();
    onClose();
  };

  const variantStyles = {
    danger: {
      icon: <AlertTriangle className="h-6 w-6 text-danger" />,
      iconBg: 'bg-danger-dim',
      confirmButton: 'bg-danger hover:bg-danger-hover hover:shadow-glow-error text-bg',
    },
    warning: {
      icon: <AlertTriangle className="h-6 w-6 text-warning" />,
      iconBg: 'bg-warning-dim',
      confirmButton: 'bg-warning hover:bg-warning/90 text-bg',
    },
    info: {
      icon: <Info className="h-6 w-6 text-primary" />,
      iconBg: 'bg-primary-dim',
      confirmButton:
        'bg-primary hover:bg-primary-hover hover:shadow-glow-primary text-bg',
    },
  };

  const styles = variantStyles[variant];

  return (
    <div
      ref={dialogRef}
      role="dialog"
      aria-modal="true"
      aria-labelledby="confirm-dialog-title"
      aria-describedby="confirm-dialog-message"
      data-testid="confirm-dialog-backdrop"
      className="fixed inset-0 z-modal flex items-center justify-center bg-bg-void/50 p-4"
    >
      <Card data-testid="confirm-dialog" className="w-full max-w-md">
        <div className="p-6">
          {/* Icon */}
          <div className={`flex justify-center mb-4`}>
            <div
              data-testid="confirm-dialog-icon"
              className={`rounded-full p-3 ${styles.iconBg}`}
            >
              {styles.icon}
            </div>
          </div>

          {/* Title */}
          <h2
            id="confirm-dialog-title"
            data-testid="confirm-dialog-title"
            className="text-base font-medium text-center text-fg mb-2"
          >
            {title}
          </h2>

          {/* Message */}
          <p
            id="confirm-dialog-message"
            data-testid="confirm-dialog-message"
            className="text-sm text-center text-fg-muted mb-6"
          >
            {message}
          </p>

          {/* Actions */}
          <div data-testid="confirm-dialog-actions" className="flex gap-3">
            <button
              ref={cancelRef}
              data-testid="confirm-dialog-cancel-button"
              onClick={onClose}
              className="flex-1 rounded-button h-9 px-4 text-sm font-medium text-fg bg-bg-elevated hover:bg-bg-surface border border-border transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              {actualCancelText}
            </button>
            <button
              data-testid={confirmButtonTestId}
              onClick={handleConfirm}
              disabled={confirmDisabled}
              className={`flex-1 rounded-button h-9 px-4 text-sm font-medium transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50 disabled:cursor-not-allowed ${styles.confirmButton}`}
            >
              {actualConfirmText}
            </button>
          </div>
        </div>
      </Card>
    </div>
  );
}
